import {
	useRef,
	useCallback,
	useEffect,
	RefObject,
	MouseEventHandler
} from 'react';

export const useResizable = <T extends HTMLElement = HTMLDivElement>(options?: {
	minWidth?: number;
	minHeight?: number;
}): {
	elementRef: RefObject<T | null>;
	resizeHandleProps: { onMouseDown: MouseEventHandler };
} => {
	const minWidth = options?.minWidth ?? 400;
	const minHeight = options?.minHeight ?? 200;

	const elementRef = useRef<T | null>(null);
	const resizing = useRef(false);
	const activeListeners = useRef<{ move: (e: MouseEvent) => void; up: () => void } | null>(null);
	const startX = useRef(0);
	const startY = useRef(0);
	const startW = useRef(0);
	const startH = useRef(0);
	const startLeft = useRef(0);
	const startTop = useRef(0);

	const onMouseDown: MouseEventHandler = useCallback((e) => {
		const element = elementRef.current;
		if (!element) return;

		const rect = element.getBoundingClientRect();

		/* Read the current transform (set by useDraggable) so we can preserve visual
		   position when switching anchoring from right/bottom → left/top. */
		const computedTransform = window.getComputedStyle(element).transform;
		const matrix = new DOMMatrix(computedTransform === 'none' ? '' : computedTransform);
		const tx = matrix.m41;
		const ty = matrix.m42;

		/* Lock in explicit dimensions at the current rendered size — no clamping here.
		   Clamping at mousedown would cause a visible snap if the panel is shorter than
		   minHeight (e.g. before results have loaded). The floor is applied per-drag below. */
		element.style.width = `${element.offsetWidth}px`;
		element.style.height = `${element.offsetHeight}px`;

		// Switch to left/top anchoring so width/height grow toward the bottom-right corner.
		element.style.left = `${rect.left - tx}px`;
		element.style.top = `${rect.top - ty}px`;
		element.style.right = 'auto';
		element.style.bottom = 'auto';

		startX.current = e.clientX;
		startY.current = e.clientY;
		startW.current = element.offsetWidth;
		startH.current = element.offsetHeight;
		startLeft.current = rect.left;
		startTop.current = rect.top;
		resizing.current = true;
		e.preventDefault();

		const onMouseMove = (moveEvent: MouseEvent) => {
			if (!resizing.current) return;

			const maxWidth = window.innerWidth - startLeft.current - 8;
			const maxHeight = window.innerHeight - startTop.current - 8;

			/* If the panel started below minWidth/minHeight (e.g. auto height before
			   results load), use the captured start size as the floor so there's no
			   snap. Once the panel is already taller than minHeight, enforce minHeight. */
			const floorW = Math.min(minWidth, startW.current);
			const floorH = Math.min(minHeight, startH.current);

			const newWidth = Math.min(
				Math.max(floorW, startW.current + (moveEvent.clientX - startX.current)),
				maxWidth
			);
			const newHeight = Math.min(
				Math.max(floorH, startH.current + (moveEvent.clientY - startY.current)),
				maxHeight
			);
			elementRef.current!.style.width = `${newWidth}px`;
			elementRef.current!.style.height = `${newHeight}px`;
		};

		const onMouseUp = () => {
			resizing.current = false;
			document.removeEventListener('mousemove', onMouseMove);
			document.removeEventListener('mouseup', onMouseUp);
			activeListeners.current = null;
		};

		activeListeners.current = { move: onMouseMove, up: onMouseUp };
		document.addEventListener('mousemove', onMouseMove);
		document.addEventListener('mouseup', onMouseUp);
	}, [minWidth, minHeight]);

	useEffect(() => {
		return () => {
			if (activeListeners.current) {
				document.removeEventListener('mousemove', activeListeners.current.move);
				document.removeEventListener('mouseup', activeListeners.current.up);
				activeListeners.current = null;
			}
		};
	}, []);

	return { elementRef, resizeHandleProps: { onMouseDown } };
};
